"use client";

import NextLink from "next/link";
import { useSearchParams } from "next/navigation";

import cn from "classnames";

import { ChevronLeftIcon } from "@/components/icons/chevronLeft.icon";
import { ForwardSlash } from "@/components/icons/forwardSlash.icon";
import { Text } from "@/components/text/text.component";

import { appendQueryString, preservePageSearchParams } from "@/utils/url.util";

import { CONTENT_MODAL_QUERY_PARAM } from "@/constants/contentModal.constants";
import { urlFor } from "@/sanity/utils";

import { RichText } from "../richText/richText.component";
import type { HeroSectionProps } from "./heroSection.component";
import { HeroSectionCta } from "./heroSectionCta.component";

const SPLIT_PATTERN_SRC = "/assets/patterns/hero-split-pattern.png";
const SPLIT_HERO_SUPERSCRIPT =
  "[&_sup]:align-baseline! [&_sup]:text-[0.65em]! [&_sup]:leading-none!";

export const SplitHeroSection = ({ block, pageName }: HeroSectionProps) => {
  const theme = block?.theme === "primary" ? "primary" : "secondary";
  const isPrimary = theme === "primary";

  const desktopImageUrl = block?.backgroundImage
    ? urlFor(block.backgroundImage).width(1440).fit("max").url()
    : null;

  const mobileImageUrl = block?.mobileBackgroundImage
    ? urlFor(block.mobileBackgroundImage)
        .width(800)
        .height(1000)
        .fit("max")
        .url()
    : desktopImageUrl;

  const isPhoneCta = block?.ctaType === "phone";
  const ctaLabel =
    isPhoneCta && block?.phoneDisplay !== "label" ? null : block?.ctaLabel;
  const ctaHref =
    !isPhoneCta && block?.ctaLink?.url != null
      ? appendQueryString(block.ctaLink.url, block.ctaLink.queryString)
      : null;

  const secondaryCtaHref =
    block?.secondaryCtaLink?.url != null
      ? appendQueryString(
          block.secondaryCtaLink.url,
          block.secondaryCtaLink.queryString,
        )
      : null;

  const hasPrimaryCta = isPhoneCta
    ? Boolean(block?.phoneNumber?.trim())
    : Boolean(ctaLabel && ctaHref);
  const hasSecondaryCta = Boolean(block?.secondaryCtaLabel && secondaryCtaHref);

  const panelBackgroundClass = isPrimary
    ? "bg-primary-100"
    : "bg-secondary-300";

  return (
    <section
      data-testid="hero-section"
      className="relative flex w-full flex-col lg:min-h-[600px]">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 hidden lg:flex">
        <div className={cn(panelBackgroundClass, "relative w-1/2")}>
          {!isPrimary ? (
            <img
              src={SPLIT_PATTERN_SRC}
              alt=""
              aria-hidden
              data-testid="hero-section-pattern"
              className="absolute inset-0 h-full w-full object-cover"
            />
          ) : null}
        </div>
        {desktopImageUrl ? (
          <div
            className="w-1/2 bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${desktopImageUrl})` }}
          />
        ) : (
          <div className="w-1/2" />
        )}
      </div>

      {desktopImageUrl ? (
        <div className="relative h-[265px] w-full lg:hidden">
          <div
            aria-hidden
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${mobileImageUrl ?? desktopImageUrl})`,
            }}
          />
        </div>
      ) : null}

      <div className="relative z-10 mx-auto flex w-full max-w-360 flex-col lg:min-h-[600px] lg:flex-row">
        <div
          className={cn(
            panelBackgroundClass,
            "relative flex flex-col justify-center px-5 py-8 lg:w-1/2 lg:justify-start lg:bg-transparent lg:px-12 lg:pt-36 lg:pb-10",
            isPrimary ? "text-secondary" : "text-white",
          )}>
          {!isPrimary ? (
            <img
              src={SPLIT_PATTERN_SRC}
              alt=""
              aria-hidden
              className="pointer-events-none absolute inset-0 h-full w-full object-cover lg:hidden"
            />
          ) : null}
          <div className="relative z-10 flex w-full max-w-[34rem] flex-1 flex-col gap-4 lg:gap-8">
            {pageName ? (
              <SplitHeroBreadcrumb name={pageName} isPrimary={isPrimary} />
            ) : null}
            <div className="flex flex-1 flex-col justify-center gap-4 lg:gap-8">
              <div className="flex flex-col gap-2">
                {block?.eyebrow ? (
                  <Text
                    variant="preHeaderMd"
                    component="p"
                    data-testid="hero-section-eyebrow"
                    className={cn(
                      "uppercase",
                      isPrimary ? "text-secondary" : "text-white",
                    )}>
                    {block.eyebrow}
                  </Text>
                ) : null}

                <div
                  className={cn(
                    "font-brown-std text-[20px] leading-[1.15] font-bold lg:mb-[25px] lg:text-[40px] [&_*]:font-bold! [&_*:not(sup)]:text-[20px]! [&_*:not(sup)]:leading-[1.15]! lg:[&_*:not(sup)]:text-[40px]!",
                    SPLIT_HERO_SUPERSCRIPT,
                    isPrimary
                      ? "text-secondary [&_*]:text-secondary!"
                      : "text-primary [&_*]:text-primary!",
                  )}
                  data-testid="hero-section-title">
                  <RichText asH1={block?.asH1}>{block?.title}</RichText>
                </div>

                {block?.subtitle ? (
                  <div
                    className={cn(
                      "font-brown-std text-[16px] leading-[1.15] font-bold lg:mb-[20px] lg:text-[32px] [&_*]:font-bold! [&_*:not(sup)]:text-[16px]! [&_*:not(sup)]:leading-[1.15]! lg:[&_*:not(sup)]:text-[32px]!",
                      SPLIT_HERO_SUPERSCRIPT,
                      isPrimary
                        ? "text-secondary [&_*]:text-secondary!"
                        : "text-white [&_*]:text-white!",
                    )}
                    data-testid="hero-section-subtitle">
                    <RichText>{block.subtitle}</RichText>
                  </div>
                ) : null}

                {block?.body ? (
                  <div
                    className={cn(
                      "max-w-xl text-[14px] leading-[1.3] font-normal lg:text-[16px] lg:leading-6 [&_*]:font-normal! [&_*:not(sup)]:text-[14px]! [&_*:not(sup)]:leading-[1.3]! lg:[&_*:not(sup)]:text-[16px]! lg:[&_*:not(sup)]:leading-6!",
                      SPLIT_HERO_SUPERSCRIPT,
                      isPrimary
                        ? "text-secondary [&_*]:text-secondary!"
                        : "text-white [&_*]:text-white!",
                    )}
                    data-testid="hero-section-body">
                    <RichText>{block.body}</RichText>
                  </div>
                ) : null}
              </div>

              <div className="flex flex-col gap-2">
                {hasPrimaryCta || hasSecondaryCta ? (
                  <div
                    data-testid="hero-section-ctas"
                    className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-center sm:gap-4">
                    {hasPrimaryCta ? (
                      <SplitHeroCta
                        href={ctaHref}
                        label={ctaLabel}
                        openInNewTab={block.ctaLink?.openInNewTab}
                        isPrimary={isPrimary}
                        phone={isPhoneCta}
                        phoneNumber={block.phoneNumber}
                      />
                    ) : null}
                    {hasSecondaryCta ? (
                      <SplitHeroCta
                        href={secondaryCtaHref}
                        label={block.secondaryCtaLabel}
                        openInNewTab={block.secondaryCtaLink?.openInNewTab}
                        isPrimary={isPrimary}
                        border
                        testId="hero-section-secondary-cta"
                      />
                    ) : null}
                  </div>
                ) : null}

                {block?.legalCallout ? (
                  <div
                    data-testid="hero-section-legal-callout"
                    className={cn(
                      "max-w-xl text-[12px] leading-[1.3] [&_*:not(sup)]:text-[12px]! [&_*:not(sup)]:leading-[1.3]! [&_a]:underline",
                      SPLIT_HERO_SUPERSCRIPT,
                      isPrimary
                        ? "text-greyscale-body [&_*]:text-greyscale-body!"
                        : "text-white [&_*]:text-white!",
                    )}>
                    <RichText interactiveQueryParam={CONTENT_MODAL_QUERY_PARAM}>
                      {block.legalCallout}
                    </RichText>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const SplitHeroCta = ({
  href,
  label,
  openInNewTab,
  isPrimary,
  border = false,
  phone = false,
  phoneNumber,
  testId = "hero-section-cta",
}: {
  href?: string | null;
  label?: string | null;
  openInNewTab?: boolean | null;
  isPrimary: boolean;
  border?: boolean;
  phone?: boolean;
  phoneNumber?: string | null;
  testId?: string;
}) => {
  const searchParams = useSearchParams();
  const hrefWithContext =
    href != null
      ? preservePageSearchParams(href, searchParams, [
          CONTENT_MODAL_QUERY_PARAM,
        ])
      : null;

  const sizeClassName =
    "mt-0 mb-[8px] px-6 py-2 text-[14px] lg:mt-2 lg:px-10 lg:py-3 lg:text-[18px]";

  // Dark (secondary) split panels use hero cyan variants; light (primary) panels keep navy CTAs.
  if (!isPrimary) {
    return (
      <HeroSectionCta
        href={hrefWithContext}
        label={label}
        openInNewTab={openInNewTab}
        phone={phone}
        phoneNumber={phoneNumber}
        variant={border ? "heroSecondary" : "heroPrimary"}
        testId={testId}
        className={sizeClassName}
      />
    );
  }

  return (
    <HeroSectionCta
      href={hrefWithContext}
      label={label}
      openInNewTab={openInNewTab}
      phone={phone}
      phoneNumber={phoneNumber}
      secondary
      border={border}
      testId={testId}
      className={sizeClassName}
    />
  );
};

const SplitHeroBreadcrumb = ({
  name,
  isPrimary,
}: {
  name: string;
  isPrimary: boolean;
}) => {
  const textClass = isPrimary ? "text-greyscale-headline" : "text-white";

  return (
    <nav
      data-testid="hero-section-breadcrumb"
      aria-label="Breadcrumb"
      className={cn(textClass, "lg:mb-0")}>
      <NextLink
        href="/"
        className="flex items-center gap-0.5 pt-2 lg:hidden"
        aria-label={`Back to Home, ${name}`}>
        <ChevronLeftIcon
          width={24}
          height={24}
          className="shrink-0"
          aria-hidden
        />
        <Text variant="body6" component="span">
          {name}
        </Text>
      </NextLink>
      <ol className="hidden items-center gap-2 lg:flex">
        <li>
          <NextLink href="/" className="underline underline-offset-2">
            <Text variant="body6" component="span" className={textClass}>
              Home
            </Text>
          </NextLink>
        </li>
        <li className="flex items-center gap-2">
          <ForwardSlash width={16} height={16} aria-hidden />
          <Text
            variant="body6"
            component="span"
            className={textClass}
            aria-current="page">
            {name}
          </Text>
        </li>
      </ol>
    </nav>
  );
};
